import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/PaymentModal.css';
import { getCurrentUser } from '../../utils/auth';
import { isIsraelUser } from '../../utils/locationUtils';
import { formatCurrency } from '../../utils/dateUtils';
import { API_URL } from '../../config/api';
import { CLOVER_ALLOWED_USERS } from '../../utils/userUtils';

const PAYMENT_METHODS = ['Cash', 'Credit Card', 'Debit Card', 'Check', 'Zelle', 'Venmo', 'Bank Transfer'];

const MultiOrderPaymentModal = ({ orders, customerId, onClose, onPaymentSuccess }) => {
  const getCurrentUserData = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  };

  const currentUsername = getCurrentUser();
  const currentUserData = getCurrentUserData();
  const isUserInIsrael = isIsraelUser(currentUserData);

  const validOrders = orders.filter(o => parseFloat(o.balance) > 0);

  // Per-order payment state: each order has its own amount and method
  const [orderPayments, setOrderPayments] = useState(() =>
    validOrders.map(o => ({
      orderNo: o.orderNo,
      balance: parseFloat(o.balance),
      amount: parseFloat(o.balance).toFixed(2),
      method: 'Cash'
    }))
  );

  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState([]);
  const [error, setError] = useState(null);
  const [isDone, setIsDone] = useState(false);

  // Clover saved cards — one selection applies to all "Credit Card" rows
  const [cloverCards, setCloverCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const isCloverEnabled = process.env.REACT_APP_CLOVER_ENABLED === 'true';
  const canAccessCloverFeature =
    isCloverEnabled &&
    process.env.REACT_APP_ENABLE_CARD_ON_FILE === 'true' &&
    CLOVER_ALLOWED_USERS.includes(currentUsername?.toLowerCase());

  const selectedCard = cloverCards.find(c => c.PaymentMethodId === selectedCardId) || null;
  const isSelectedCardDebit = selectedCard?.IsDebit === 1 || selectedCard?.IsDebit === true;

  useEffect(() => {
    if (!customerId || !isCloverEnabled) return;
    axios
      .get(`${API_URL}/api/clover/customer/${customerId}/cards`)
      .then(res => {
        if (res.data.success) {
          setCloverCards(res.data.cards || []);
        }
      })
      .catch(() => {});
  }, [customerId, isCloverEnabled]);

  const updateRow = (orderNo, field, value) => {
    setOrderPayments(prev =>
      prev.map(op => op.orderNo === orderNo ? { ...op, [field]: value } : op)
    );
  };

  const fillAllToBalance = () => {
    setOrderPayments(prev =>
      prev.map(op => ({ ...op, amount: op.balance.toFixed(2) }))
    );
  };

  // The $150 threshold applies to the combined Credit Card payment, not to each order
  const creditCardTotal = orderPayments.reduce(
    (sum, op) => sum + (op.method === 'Credit Card' ? (parseFloat(op.amount) || 0) : 0),
    0
  );

  // Returns the 3% fee for a single order row (Credit Card only, non-Israeli, combined CC total over $150)
  const getRowFee = (op) => {
    const amt = parseFloat(op.amount) || 0;
    if (isUserInIsrael) return 0;
    // Row uses Credit Card — check if Clover debit overrides fee
    if (op.method === 'Credit Card') {
      if (selectedCardId && isSelectedCardDebit) return 0; // debit card, no fee
      if (creditCardTotal >= 150) return parseFloat((amt * 0.03).toFixed(2));
    }
    return 0;
  };

  const totalApplied = orderPayments.reduce((sum, op) => sum + (parseFloat(op.amount) || 0), 0);
  const totalFees = orderPayments.reduce((sum, op) => sum + getRowFee(op), 0);
  const totalCharge = totalApplied + totalFees;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const rowsToProcess = orderPayments.filter(op => parseFloat(op.amount) > 0);
    if (rowsToProcess.length === 0) {
      setError('Please enter an amount for at least one order');
      return;
    }

    setIsProcessing(true);
    setError(null);

    // Phase 1: Charge Clover card per order (only for Credit Card rows with a saved card).
    // These are individual card charges — still one-at-a-time, but that's fine for Clover.
    const chargeMap = {}; // orderNo → { cloverTransactionId, cloverLast4 }
    for (const op of rowsToProcess) {
      if (!selectedCardId || op.method !== 'Credit Card') continue;
      const amount = parseFloat(op.amount);
      const fee = getRowFee(op);
      try {
        const chargeResponse = await axios.post(`${API_URL}/api/clover/charge-card`, {
          paymentMethodId: selectedCardId,
          amount: parseFloat((amount + fee).toFixed(2)),
          orderNo: op.orderNo,
          username: currentUsername
        });
        if (!chargeResponse.data.success) {
          throw new Error(chargeResponse.data.error || 'Card charge failed');
        }
        chargeMap[op.orderNo] = {
          cloverTransactionId: chargeResponse.data.transaction?.transactionId || null,
          cloverLast4: chargeResponse.data.transaction?.card?.lastFour || null
        };
      } catch (err) {
        // Mark this order as failed so it's excluded from the batch insert below
        chargeMap[op.orderNo] = { error: err.message };
      }
    }

    // Phase 2: Build the batch payload. Skip any order where the Clover charge failed.
    const batchPayments = rowsToProcess
      .filter(op => {
        const charge = chargeMap[op.orderNo];
        return !charge?.error; // only include if no Clover error
      })
      .map(op => ({
        orderNo: op.orderNo,
        paymentMethod: op.method,
        amount: parseFloat(op.amount),
        cardFee: getRowFee(op),
        notes: notes || null,
        cloverTransactionId: chargeMap[op.orderNo]?.cloverTransactionId || null,
        cardLast4: chargeMap[op.orderNo]?.cloverLast4 || null
      }));

    // Build results for orders that failed Clover charge (so user sees the errors)
    const cloverFailResults = rowsToProcess
      .filter(op => chargeMap[op.orderNo]?.error)
      .map(op => ({
        orderNo: op.orderNo,
        amount: parseFloat(op.amount),
        method: op.method,
        status: 'error',
        message: `Card charge failed: ${chargeMap[op.orderNo].error}`
      }));

    let batchResults = [];

    if (batchPayments.length > 0) {
      try {
        // Phase 3: ONE call to the backend batch endpoint — inserts all records into DB directly.
        // This avoids calling the n8n webhook per order (n8n only handles one at a time).
        const response = await axios.post(`${API_URL}/api/payments/batch`, {
          payments: batchPayments,
          user: currentUsername || 'DiniV2System',
          isIsraelUser: isUserInIsrael,
          userLocation: currentUserData?.UserLocation || 'Unknown'
        });

        batchResults = (response.data.results || []).map(r => ({
          ...r,
          method: rowsToProcess.find(op => String(op.orderNo) === String(r.orderNo))?.method || ''
        }));
      } catch (err) {
        // If the whole batch request fails, mark everything as failed
        batchResults = batchPayments.map(p => ({
          orderNo: p.orderNo,
          amount: p.amount,
          method: rowsToProcess.find(op => String(op.orderNo) === String(p.orderNo))?.method || '',
          status: 'error',
          message: err.message
        }));
      }
    }

    const allResults = [...cloverFailResults, ...batchResults];
    setProcessingResults(allResults);
    setIsProcessing(false);
    setIsDone(true);

    const successCount = allResults.filter(r => r.status === 'success').length;
    if (successCount > 0) {
      onPaymentSuccess({ success: true, results: allResults, successCount });
    }
  };

  const inputStyle = {
    background: 'var(--dark-bg, #1a1a2e)',
    border: '1px solid var(--border-color, #444)',
    color: 'var(--text-primary, #e0e0e0)',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '13px',
    width: '100%'
  };

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal-content" style={{ maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="payment-modal-header">
          <h5 className="payment-modal-title">
            <i className="fas fa-credit-card payment-icon"></i>
            Pay Multiple Orders ({validOrders.length})
          </h5>
          <button
            type="button"
            onClick={onClose}
            className="payment-modal-close"
            disabled={isProcessing}
          >
            ×
          </button>
        </div>

        {/* Processing results (shown while processing and after done) */}
        {processingResults.length > 0 && (
          <div style={{ padding: '8px 20px 0' }}>
            {processingResults.map(r => (
              <div
                key={r.orderNo}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  marginBottom: '4px',
                  borderRadius: '6px',
                  backgroundColor: r.status === 'success' ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)',
                  border: `1px solid ${r.status === 'success' ? '#28a745' : '#dc3545'}`
                }}
              >
                <i
                  className={`fas fa-${r.status === 'success' ? 'check-circle' : 'times-circle'}`}
                  style={{ color: r.status === 'success' ? '#28a745' : '#dc3545' }}
                />
                <span style={{ fontSize: '13px' }}>
                  Order #{r.orderNo} ({r.method}):{' '}
                  {r.status === 'success'
                    ? `${formatCurrency(r.amount)} recorded`
                    : r.message}
                </span>
              </div>
            ))}
          </div>
        )}

        {isDone ? (
          <div style={{ padding: '24px 20px', textAlign: 'center' }}>
            {processingResults.every(r => r.status === 'success') ? (
              <div style={{ color: '#28a745' }}>
                <i className="fas fa-check-circle" style={{ fontSize: '36px', marginBottom: '10px', display: 'block' }} />
                All {processingResults.length} payments processed successfully
              </div>
            ) : (
              <div style={{ color: '#ffc107' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '36px', marginBottom: '10px', display: 'block' }} />
                {processingResults.filter(r => r.status === 'success').length} of{' '}
                {processingResults.length} payments succeeded
              </div>
            )}
            <button className="payment-btn payment-btn-secondary" onClick={onClose} style={{ marginTop: '16px' }}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="payment-form" style={{ paddingTop: '8px' }}>
            {error && (
              <div className="payment-error-alert">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {error}
              </div>
            )}

            {/* Clover saved cards */}
            {canAccessCloverFeature && cloverCards.length > 0 && (
              <div className="payment-form-group">
                <label className="payment-label" style={{ marginBottom: '6px' }}>
                  <i className="fas fa-credit-card" style={{ marginRight: '6px', color: '#007bff' }}></i>
                  Saved Card (applies to all Credit Card rows)
                </label>
                {cloverCards.map(card => {
                  const isSelected = selectedCardId === card.PaymentMethodId;
                  const expired = !!card.IsExpired;
                  return (
                    <div
                      key={card.PaymentMethodId}
                      onClick={() => {
                        if (expired) return;
                        const nextSelected = isSelected ? null : card.PaymentMethodId;
                        setSelectedCardId(nextSelected);
                        setOrderPayments(prev =>
                          prev.map(op => ({ ...op, method: nextSelected ? 'Credit Card' : 'Cash' }))
                        );
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        marginBottom: '6px',
                        borderRadius: '8px',
                        border: `2px solid ${isSelected ? '#007bff' : 'var(--border-color, #444)'}`,
                        backgroundColor: isSelected ? 'rgba(0,123,255,0.08)' : 'var(--dark-bg)',
                        cursor: expired ? 'not-allowed' : 'pointer',
                        opacity: expired ? 0.5 : 1,
                        transition: 'border-color 0.15s, background-color 0.15s'
                      }}
                    >
                      <i className="fas fa-credit-card" style={{ color: isSelected ? '#007bff' : '#888' }} />
                      <div style={{ flex: 1, fontSize: '14px' }}>
                        <span style={{ fontWeight: 500 }}>{card.CardBrand} •••• {card.Last4}</span>
                        <span style={{
                          marginLeft: '6px',
                          backgroundColor: card.IsDebit ? '#6f42c1' : '#007bff',
                          color: 'white',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {card.IsDebit ? 'DEBIT' : 'CREDIT'}
                        </span>
                        <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>
                          Expires {card.ExpMonth}/{card.ExpYear}
                        </span>
                      </div>
                      {isSelected && <i className="fas fa-check-circle" style={{ color: '#007bff' }} />}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Per-order table */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="payment-label" style={{ margin: 0 }}>Order Payments</label>
                <button
                  type="button"
                  onClick={fillAllToBalance}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border-color, #444)',
                    color: 'var(--text-primary, #888)',
                    fontSize: '12px',
                    padding: '3px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Fill all to balance
                </button>
              </div>

              {/* Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '90px 100px 1fr 130px',
                gap: '8px',
                padding: '6px 4px',
                borderBottom: '1px solid var(--border-color, #444)',
                fontSize: '12px',
                color: '#888',
                fontWeight: 500
              }}>
                <span>Order #</span>
                <span style={{ textAlign: 'right' }}>Balance Due</span>
                <span style={{ paddingLeft: '4px' }}>Method</span>
                <span style={{ textAlign: 'right' }}>Amount</span>
              </div>

              {/* Rows */}
              {orderPayments.map(op => {
                const fee = getRowFee(op);
                const amt = parseFloat(op.amount) || 0;
                return (
                  <div key={op.orderNo}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '90px 100px 1fr 130px',
                      gap: '8px',
                      padding: '8px 4px',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      alignItems: 'center'
                    }}>
                      {/* Order number */}
                      <span style={{ fontWeight: 500, fontSize: '14px' }}>#{op.orderNo}</span>

                      {/* Balance */}
                      <span style={{ textAlign: 'right', fontSize: '14px', color: 'var(--text-primary)' }}>
                        {formatCurrency(op.balance)}
                      </span>

                      {/* Method dropdown */}
                      <select
                        value={op.method}
                        onChange={e => updateRow(op.orderNo, 'method', e.target.value)}
                        style={{ ...inputStyle }}
                        disabled={isProcessing}
                      >
                        {PAYMENT_METHODS.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>

                      {/* Amount input with Max button */}
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <span style={{
                            position: 'absolute',
                            left: '7px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--text-primary)',
                            fontSize: '13px'
                          }}>$</span>
                          <input
                            type="text"
                            value={op.amount}
                            onChange={e => {
                              if (/^\d*\.?\d*$/.test(e.target.value)) {
                                updateRow(op.orderNo, 'amount', e.target.value);
                              }
                            }}
                            style={{ ...inputStyle, paddingLeft: '18px' }}
                            placeholder="0.00"
                            disabled={isProcessing}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => updateRow(op.orderNo, 'amount', op.balance.toFixed(2))}
                          title="Fill to balance"
                          disabled={isProcessing}
                          style={{
                            background: 'none',
                            border: '1px solid var(--border-color, #444)',
                            color: 'var(--text-primary, #888)',
                            fontSize: '11px',
                            padding: '4px 6px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          Max
                        </button>
                      </div>
                    </div>

                    {/* Fee row for this order */}
                    {fee > 0 && amt > 0 && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '90px 100px 1fr 130px',
                        gap: '8px',
                        padding: '2px 4px 6px',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        fontSize: '12px',
                        color: '#888'
                      }}>
                        <span></span>
                        <span></span>
                        <span style={{ paddingLeft: '4px' }}>3% processing fee</span>
                        <span style={{ textAlign: 'right', color: '#ffc107' }}>+{formatCurrency(fee)}</span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Total row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '90px 100px 1fr 130px',
                gap: '8px',
                padding: '10px 4px 4px',
                borderTop: '2px solid var(--border-color, #444)',
                fontWeight: 600,
                fontSize: '14px'
              }}>
                <span>Total</span>
                <span></span>
                <span style={{ paddingLeft: '4px', fontSize: '12px', color: '#888', fontWeight: 400 }}>
                  {totalFees > 0 && `incl. ${formatCurrency(totalFees)} fees`}
                </span>
                <span style={{ textAlign: 'right', color: totalCharge > 0 ? '#28a745' : 'inherit' }}>
                  {formatCurrency(totalCharge)}
                </span>
              </div>
            </div>

            {isUserInIsrael && (
              <div style={{ fontSize: '12px', color: '#17a2b8', marginBottom: '8px' }}>
                <i className="fas fa-globe" style={{ marginRight: '4px' }}></i>
                No processing fees for Israeli users
              </div>
            )}

            {/* Notes */}
            <div className="payment-form-group">
              <label className="payment-label">Notes (applies to all orders)</label>
              <textarea
                className="payment-textarea"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional payment notes..."
                rows={2}
                disabled={isProcessing}
              />
            </div>

            {isProcessing && (
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>
                Processing order {processingResults.length + 1} of{' '}
                {orderPayments.filter(op => parseFloat(op.amount) > 0).length}...
              </div>
            )}

            <div className="payment-modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="payment-btn payment-btn-secondary"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="payment-btn payment-btn-primary"
                disabled={isProcessing || totalApplied <= 0}
              >
                {isProcessing ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check mr-2"></i>
                    {totalFees > 0
                      ? `Process ${formatCurrency(totalCharge)} (${orderPayments.filter(op => parseFloat(op.amount) > 0).length} orders)`
                      : `Process ${formatCurrency(totalApplied)} (${orderPayments.filter(op => parseFloat(op.amount) > 0).length} orders)`
                    }
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MultiOrderPaymentModal;
